import React from "react";
import { AlertCircle, TrendingUp } from "lucide-react";

/**
 * Usage Indicator Component
 * Displays AI usage quota and warnings
 */
const UsageIndicator = ({ usageInfo }) => {
  if (!usageInfo) {
    // Minimal placeholder to reserve space and keep layout stable
    return (
      <div className="p-2 rounded-md border bg-gray-50 border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">AI Usage</span>
          <span className="text-xs text-gray-400">-- / --</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1" />
      </div>
    );
  }

  const {
    requestsToday,
    dailyLimit,
    percentUsed,
    requestsThisMinute,
    minuteLimit,
  } = usageInfo;

  // Determine status
  const atLimit = requestsToday >= dailyLimit;
  const nearLimit = percentUsed >= 80;
  const warning = atLimit || nearLimit;

  // Color scheme based on usage
  const getColorClasses = () => {
    if (atLimit) {
      return {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-700",
        bar: "bg-red-500",
        icon: "text-red-500",
      };
    } else if (nearLimit) {
      return {
        bg: "bg-yellow-50",
        border: "border-yellow-200",
        text: "text-yellow-700",
        bar: "bg-yellow-500",
        icon: "text-yellow-500",
      };
    } else {
      return {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        bar: "bg-blue-500",
        icon: "text-blue-500",
      };
    }
  };

  const colors = getColorClasses();

  return (
    <div
      className={`p-2 rounded-md border ${colors.bg} ${colors.border} transition-all duration-200 sticky top-0 z-10`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {warning ? (
            <AlertCircle size={16} className={colors.icon} />
          ) : (
            <TrendingUp size={16} className={colors.icon} />
          )}
          <span className={`text-xs font-medium ${colors.text}`}>
            AI Usage Today
          </span>
        </div>
        <span className={`text-xs font-bold ${colors.text}`}>
          {requestsToday} / {dailyLimit}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1 overflow-hidden">
        <div
          className={`h-full ${colors.bar} transition-all duration-300 ease-out`}
          style={{ width: `${Math.min(percentUsed, 100)}%` }}
        />
      </div>

      {/* Compact footer */}
      <div className="flex justify-between text-[10px] text-gray-500">
        <span>
          This minute: {requestsThisMinute}/{minuteLimit} requests
        </span>
        <span>{Math.max(dailyLimit - requestsToday, 0)} left today</span>
      </div>
    </div>
  );
};

export default UsageIndicator;
